/**
 * @fileoverview Connect explicitly selected Drive folders to the backend duplicate-file review workflow. The workspace owns submission intent and feedback; the backend owns scan execution, identity, and permissions. A successful request is distinct from reviewed duplicates or an authorized file deletion.
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppPageHeader, AppTextInput, AppToggle } from '../shared';
import './driveDedup.css';

const N8N_DOCUMENTATION_URL = 'https://drive.google.com/drive/u/1/folders/1ZsHAPQWisrBBufveK5lKuTdZ1RhyxYYI';

const INITIAL_FORM = {
  folderLink: '',
  searchSubfolders: true,
  pdfContentCheck: true,
  fuzzySearch: false,
};



function optionLabel(value) {
  return value ? 'On' : 'Off';
}



function DriveDedup() {
  const showToast = useContext(ToastContext);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);

  useEffect(() => {
    document.title = 'Drive Dedup';
  }, []);

  const normalizedFolderLink = form.folderLink.trim();
  const canSubmit = useMemo(() => normalizedFolderLink.length > 0 && !submitting, [normalizedFolderLink, submitting]);

  const updateField =

  (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitScan =

  (event) => {
    event.preventDefault();
    if (!normalizedFolderLink) {
      showToast({ severity: 'warn', summary: 'Folder Link Required', detail: 'Paste a Google Drive folder link before starting the scan.' });
      return;
    }

    const payload = {
      folderLink: normalizedFolderLink,
      searchSubfolders: Boolean(form.searchSubfolders),
      pdfContentCheck: Boolean(form.pdfContentCheck),
      fuzzySearch: Boolean(form.fuzzySearch),
    };

    setSubmitting(true);
    api.post('/utilities/drive-dedup/scan', payload)
      .then((response) => {
        setLastSubmission(response.data || { status: 'submitted', payload });
        showToast({ severity: 'success', summary: 'Drive Dedup Started', detail: 'The scan request was sent.' });
      })
      .catch((error) => {
        const detail = error?.response?.data?.error || error.message;
        showToast({ severity: 'error', summary: 'Drive Dedup Failed', detail });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="drive-dedup-page">
      <AppPageHeader
        eyebrow="Workspace Tools"
        title="Drive Dedup"
        icon="pi pi-copy text-xl"
        actions={(
          <a
            className="drive-dedup-documentation-link"
            href={N8N_DOCUMENTATION_URL}
            target="_blank"
            rel="noreferrer"
          >
            <i className="pi pi-external-link" aria-hidden="true" />
            <span>n8n Documentation</span>
          </a>
        )}
      />

      <div className="drive-dedup-content">
        <form className="drive-dedup-panel" onSubmit={submitScan}>
          <div className="drive-dedup-panel__header">
            <div>
              <h2>Google Drive Folder Scan</h2>
              <p>Submit an Active Cases folder or subfolder for duplicate-file review.</p>
            </div>
            <span className="drive-dedup-panel__icon">
              <i className="pi pi-folder-open" aria-hidden="true" />
            </span>
          </div>

          <label className="drive-dedup-field" htmlFor="drive-dedup-folder-link">
            <span>Folder link</span>
            <AppTextInput
              id="drive-dedup-folder-link"
              value={form.folderLink}
              onChange={(event) => updateField('folderLink', event.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              disabled={submitting}
              autoComplete="off"
            />
          </label>

          <div className="drive-dedup-options" aria-label="Drive dedup scan options">
            <ToggleRow
              label="Search subfolders"
              detail="Include child folders under the submitted folder."
              checked={form.searchSubfolders}
              disabled={submitting}
              onChange={(value) => updateField('searchSubfolders', value)}
            />
            <ToggleRow
              label="Flag PDFs with identical content but different metadata"
              detail="Compare PDF text/content beyond file names."
              checked={form.pdfContentCheck}
              disabled={submitting}
              onChange={(value) => updateField('pdfContentCheck', value)}
            />
            <ToggleRow
              label="Flag files that may be duplicates"
              detail="Use approximate matching for near-duplicate candidates."
              checked={form.fuzzySearch}
              disabled={submitting}
              onChange={(value) => updateField('fuzzySearch', value)}
            />
          </div>

          <div className="drive-dedup-actions">
            <AppButton
              type="submit"
              label={submitting ? 'Submitting Scan' : 'Start Scan'}
              icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
              disabled={!canSubmit}
            />
          </div>
        </form>

        <aside className="drive-dedup-summary" aria-label="Drive dedup scan summary">
          <h2>Request Summary</h2>
          <dl>
            <div>
              <dt>Search subfolders</dt>
              <dd>{optionLabel(form.searchSubfolders)}</dd>
            </div>
            <div>
              <dt>PDF content check</dt>
              <dd>{optionLabel(form.pdfContentCheck)}</dd>
            </div>
            <div>
              <dt>Fuzzy search</dt>
              <dd>{optionLabel(form.fuzzySearch)}</dd>
            </div>
            <div>
              <dt>Slack recipient</dt>
              <dd>Current login</dd>
            </div>
          </dl>

          {lastSubmission && (
            <div className="drive-dedup-submitted">
              <i className="pi pi-check-circle" aria-hidden="true" />
              <div>
                <strong>Scan submitted</strong>
                <span>{lastSubmission.payload?.folderLink || normalizedFolderLink}</span>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}



function ToggleRow({ label, detail, checked, disabled, onChange }) {
  const id = `drive-dedup-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className="drive-dedup-toggle-row">
      <div>
        <label htmlFor={id}>{label}</label>
        <span>{detail}</span>
      </div>
      <AppToggle
        id={id}
        checked={checked}
        disabled={disabled}
        ariaLabel={label}
        onChange={(event) => onChange(Boolean(event.value))}
      />
    </div>
  );
}

export default DriveDedup;
